import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "dashboard-charts" }
const _hoisted_2 = { class: "charts-grid" }
const _hoisted_3 = { class: "chart-header-main" }
const _hoisted_4 = { class: "chart-sub-title" }
const _hoisted_5 = { class: "chart-header-main" }

import { ref, onMounted, watch } from 'vue';
import { Chart, registerables } from 'chart.js';

interface WageMagamData {
  wm_ym: string;
  wm_job_type: string;
  wm_isuse: 'y' | 'n';
  wm_count: string;
  wm_avg_age: string;
  wm_wage_sum: string;
  wm_emp_deduction: string;
  wm_center_deduction: string;
  wm_non_taxable: string;
  wm_emp_savings: string;
  wm_center_savings: string;
  wm_center_profit: string;
  wm_suga_sum?: string;
  wm_pacient_count?: string;
}

interface MonthlyOverallSummary {
  month: string;
  totalCount: number; // 전체 인원 수 (참고용)
  avgAge: number;
  totalWageSum: number;
  totalEmpDeduction: number;
  totalCenterDeduction: number;
  totalNonTaxable: number;
  totalEmpSavings: number;
  totalCenterSavings: number;
  totalCenterProfit: number;
  totalSugaSum: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'GraphChart',
  props: {
    chartData: {}
  },
  setup(__props: any) {

Chart.register(...registerables);

const props = __props;

// 숫자 변환 유틸
const toNumber = (value: string | null | undefined): number => {
  if (value === null || value === undefined) return 0;
  const num = Number(value.toString().replace(/,/g, ''));
  return Number.isNaN(num) ? 0 : num;
};

// 차트 ref
const totalComparisonChart = ref<HTMLCanvasElement | null>(null);
const caregiverCountChart = ref<HTMLCanvasElement | null>(null);

// 차트 인스턴스
let totalComparisonChartInstance: Chart | null = null;
let caregiverCountChartInstance: Chart | null = null;

// 누적 금액과 비율 정보
const totalRatioInfo = ref<{
  totalSuga: number;
  totalWage: number;
  ratio: string;
} | null>(null);

// 월별 전체합계 요약 (가능하면 wm_job_type === '전체합계' 기준)
const getMonthlyOverall = (): Record<string, MonthlyOverallSummary> => {
  const monthlyMap: Record<
    string,
    MonthlyOverallSummary & { ageWeightedSum: number; ageWeight: number }
  > = {};

  // 우선 전체합계만 사용, 없으면 전체 데이터 사용
  const overallRows = props.chartData.filter(
    (item) => item.wm_job_type === '전체합계',
  );
  const source = overallRows.length > 0 ? overallRows : props.chartData;

  source.forEach((item) => {
    if (!item.wm_ym || item.wm_ym.length < 6) return;

    const month = item.wm_ym.substring(4, 6); // YYYYMM -> MM

    if (!monthlyMap[month]) {
      monthlyMap[month] = {
        month,
        totalCount: 0,
        avgAge: 0,
        totalWageSum: 0,
        totalEmpDeduction: 0,
        totalCenterDeduction: 0,
        totalNonTaxable: 0,
        totalEmpSavings: 0,
        totalCenterSavings: 0,
        totalCenterProfit: 0,
        totalSugaSum: 0,
        ageWeightedSum: 0,
        ageWeight: 0,
      };
    }

    const m = monthlyMap[month];

    const count = toNumber(item.wm_count);
    const avgAge = Number(item.wm_avg_age);

    m.totalCount += count;
    m.totalWageSum += toNumber(item.wm_wage_sum);
    m.totalEmpDeduction += toNumber(item.wm_emp_deduction);
    m.totalCenterDeduction += toNumber(item.wm_center_deduction);
    m.totalNonTaxable += toNumber(item.wm_non_taxable);
    m.totalEmpSavings += toNumber(item.wm_emp_savings);
    m.totalCenterSavings += toNumber(item.wm_center_savings);
    m.totalCenterProfit += toNumber(item.wm_center_profit);
    m.totalSugaSum += toNumber(item.wm_suga_sum);

    if (!Number.isNaN(avgAge) && avgAge > 0) {
      const weight = count > 0 ? count : 1;
      m.ageWeightedSum += avgAge * weight;
      m.ageWeight += weight;
    }
  });

  Object.values(monthlyMap).forEach((m) => {
    if (m.ageWeight > 0) {
      m.avgAge = m.ageWeightedSum / m.ageWeight;
    } else {
      m.avgAge = 0;
    }
  });

  return monthlyMap;
};

// 1) 총 수가와 인건비총합 비교
const createTotalComparisonChart = () => {
  if (!totalComparisonChart.value) return;

  const monthlyData = getMonthlyOverall();
  const labels = Array.from({ length: 12 }, (_, i) => `${i + 1}월`);
  const sugaAmountData: number[] = [];
  const wageSumData: number[] = [];
  const ratioData: number[] = [];

  let totalSuga = 0;
  let totalWage = 0;

  for (let i = 0; i < 12; i += 1) {
    const monthKey = String(i + 1).padStart(2, '0');
    const month = monthlyData[monthKey];
    
    // 총 수가금액 (만원 단위) - 데이터베이스에서 가져온 값
    const sugaAmount = (month?.totalSugaSum || 0) / 10000;
    sugaAmountData.push(sugaAmount);
    
    // 인건비총합 (만원 단위) - 기관공제합 제거
    const wageSum = (month?.totalWageSum || 0) / 10000;
    wageSumData.push(wageSum);
    
    // 비율 계산 (인건비총합 / 총 수가금액 * 100)
    const ratio = sugaAmount > 0 ? (wageSum / sugaAmount) * 100 : 0;
    ratioData.push(ratio);
    
    // 누적 금액 계산
    totalSuga += sugaAmount;
    totalWage += wageSum;
  }

  // 누적 비율 계산
  const totalRatio = totalSuga > 0 ? (totalWage / totalSuga) * 100 : 0;
  totalRatioInfo.value = {
    totalSuga: Math.round(totalSuga * 100) / 100,
    totalWage: Math.round(totalWage * 100) / 100,
    ratio: totalRatio.toFixed(2)
  };

  // Y축 최소값 계산 (데이터가 모두 일정 기준 이상이면 중간 지점부터 시작)
  const allAmounts = [...sugaAmountData, ...wageSumData].filter(v => v > 0);
  const minAmount = allAmounts.length > 0 ? Math.min(...allAmounts) : 0;
  const maxAmount = allAmounts.length > 0 ? Math.max(...allAmounts) : 0;
  const range = maxAmount - minAmount;
  
  // 데이터 범위가 충분히 크면 (최소값이 최대값의 50% 이상이면) 중간 지점부터 시작
  let yMin = 0;
  if (minAmount > 0 && range > 0 && minAmount / maxAmount >= 0.5) {
    yMin = Math.floor(minAmount * 0.8); // 최소값의 80%부터 시작
  }

  if (totalComparisonChartInstance) {
    totalComparisonChartInstance.destroy();
  }

  totalComparisonChartInstance = new Chart(totalComparisonChart.value, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: '총 수가',
          data: sugaAmountData,
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          borderWidth: 2,
          tension: 0.3,
          fill: true,
          yAxisID: 'y',
        },
        {
          label: '인건비총합',
          data: wageSumData,
          borderColor: '#8b5cf6',
          backgroundColor: 'rgba(139, 92, 246, 0.15)',
          borderWidth: 2,
          tension: 0.3,
          fill: true,
          yAxisID: 'y',
        },
        {
          label: '비율 (%)',
          data: ratioData,
          borderColor: '#f59e0b',
          backgroundColor: 'rgba(245, 158, 11, 0.15)',
          borderWidth: 2,
          tension: 0.3,
          fill: false,
          yAxisID: 'y1',
          borderDash: [5, 5],
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: {
        mode: 'index',
        intersect: false,
      },
      plugins: {
        legend: { 
          display: true, 
          position: 'top',
          labels: {
            font: {
              size: 11
            },
            padding: 8
          }
        },
        tooltip: {
          titleFont: {
            size: 11
          },
          bodyFont: {
            size: 10
          },
          padding: 8,
          callbacks: {
            label: (context) => {
              const value = context.parsed.y || 0;
              if (context.datasetIndex === 2) {
                // 비율 데이터셋
                return `${context.dataset.label}: ${value.toFixed(1)}%`;
              } else {
                // 금액 데이터셋
                return `${context.dataset.label}: ${value.toLocaleString()}만`;
              }
            },
          },
        },
      },
      scales: {
        x: {
          ticks: {
            font: {
              size: 10
            },
            maxRotation: 45,
            minRotation: 0
          }
        },
        y: {
          type: 'linear',
          position: 'left',
          beginAtZero: yMin === 0,
          min: yMin,
          title: { 
            display: true, 
            text: '금액(만원)',
            font: {
              size: 10
            }
          },
          ticks: {
            font: {
              size: 9
            },
            maxTicksLimit: 5,
            callback: (value) => {
              const num = Number(value);
              if (num >= 1000) {
                return `${(num / 1000).toFixed(1)}천만`;
              }
              return `${num.toLocaleString()}만`;
            },
          },
        },
        y1: {
          type: 'linear',
          position: 'right',
          beginAtZero: true,
          title: { 
            display: true, 
            text: '비율(%)',
            font: {
              size: 10
            }
          },
          grid: {
            drawOnChartArea: false,
          },
          ticks: {
            font: {
              size: 9
            },
            maxTicksLimit: 5,
            callback: (value) => `${Number(value).toFixed(0)}%`,
          },
        },
      },
    },
  });
};

// 2) 요양보호사와 수급자 수 추이
const createCaregiverCountChart = () => {
  if (!caregiverCountChart.value) return;

  const labels = Array.from({ length: 12 }, (_, i) => `${i + 1}월`);
  
  // 월별 요양보호사 수와 수급자 수
  const caregiverMonthly: number[] = Array(12).fill(0);
  const patientMonthly: number[] = Array(12).fill(0);

  props.chartData.forEach((item) => {
    if (!item.wm_ym || item.wm_ym.length < 6) return;
    if (!item.wm_job_type) return;
    
    // 요양보호사 데이터만 필터링
    if (item.wm_job_type !== '요양보호사') return;

    const monthIndex = Number(item.wm_ym.substring(4, 6)) - 1;
    if (monthIndex < 0 || monthIndex > 11) return;

    // 요양보호사 수
    caregiverMonthly[monthIndex] += toNumber(item.wm_count);
    
    // 수급자 수
    patientMonthly[monthIndex] += toNumber(item.wm_pacient_count);
  });

  // Y축 최소값 계산 (데이터가 모두 일정 기준 이상이면 중간 지점부터 시작)
  const allCounts = [...caregiverMonthly, ...patientMonthly].filter(v => v > 0);
  const minCount = allCounts.length > 0 ? Math.min(...allCounts) : 0;
  const maxCount = allCounts.length > 0 ? Math.max(...allCounts) : 0;
  const countRange = maxCount - minCount;
  
  // 데이터 범위가 충분히 크면 (최소값이 최대값의 50% 이상이면) 중간 지점부터 시작
  let yMinCount = 0;
  if (minCount > 0 && countRange > 0 && minCount / maxCount >= 0.5) {
    yMinCount = Math.floor(minCount * 0.8); // 최소값의 80%부터 시작
  }

  if (caregiverCountChartInstance) {
    caregiverCountChartInstance.destroy();
  }

  caregiverCountChartInstance = new Chart(caregiverCountChart.value, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: '요양보호사',
          data: caregiverMonthly,
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          borderWidth: 2,
          tension: 0.3,
          fill: true,
        },
        {
          label: '수급자',
          data: patientMonthly,
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.15)',
          borderWidth: 2,
          tension: 0.3,
          fill: true,
        },
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { 
          display: true, 
          position: 'top',
          labels: {
            font: {
              size: 11
            },
            padding: 8
          }
        },
        tooltip: {
          titleFont: {
            size: 11
          },
          bodyFont: {
            size: 10
          },
          padding: 8,
          callbacks: {
            label: (context) => {
              const value = context.parsed.y || 0;
              return `${context.dataset.label}: ${value.toLocaleString()}명`;
            },
          },
        },
      },
      scales: {
        x: {
          ticks: {
            font: {
              size: 10
            },
            maxRotation: 45,
            minRotation: 0
          }
        },
        y: {
          beginAtZero: yMinCount === 0,
          min: yMinCount,
          title: { 
            display: true, 
            text: '인원 수(명)',
            font: {
              size: 10
            }
          },
          ticks: {
            font: {
              size: 9
            },
            maxTicksLimit: 5,
            callback: (value) => `${Number(value).toLocaleString()}명`,
          },
        },
      },
    },
  });
};

// 모든 차트 생성
const createAllCharts = () => {
  createTotalComparisonChart();
  createCaregiverCountChart();
};

// chartData 변경 감지
watch(
  () => props.chartData,
  () => {
    createAllCharts();
  },
  { deep: true },
);

onMounted(() => {
  createAllCharts();
});

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_card_title = _resolveComponent("v-card-title")!
  const _component_v_card_text = _resolveComponent("v-card-text")!
  const _component_v_card = _resolveComponent("v-card")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_v_card, {
        class: "chart-card",
        elevation: "0"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "chart-header" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_3, [
                _createVNode(_component_v_icon, {
                  color: "#1e3a8a",
                  class: "chart-header-icon"
                }, {
                  default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                    _createTextVNode("mdi-chart-line", -1)
                  ]))]),
                  _: 1
                }),
                _cache[1] || (_cache[1] = _createElementVNode("span", { class: "chart-title-text" }, "총 수가와 인건비총합 비교", -1))
              ]),
              _createElementVNode("span", _hoisted_4, "월별 총 수가와 인건비총합 비교 (비율: " + _toDisplayString(totalRatioInfo.value?.ratio ? totalRatioInfo.value.ratio + '%' : '0%') + ")", 1)
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_text, null, {
            default: _withCtx(() => [
              _createElementVNode("canvas", {
                ref_key: "totalComparisonChart",
                ref: totalComparisonChart
              }, null, 512)
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_v_card, {
        class: "chart-card",
        elevation: "0"
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_card_title, { class: "chart-header" }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_component_v_icon, {
                  color: "#1e3a8a",
                  class: "chart-header-icon"
                }, {
                  default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                    _createTextVNode("mdi-account-group", -1)
                  ]))]),
                  _: 1
                }),
                _cache[3] || (_cache[3] = _createElementVNode("span", { class: "chart-title-text" }, "요양보호사와 수급자 수 추이", -1))
              ]),
              _cache[4] || (_cache[4] = _createElementVNode("span", { class: "chart-sub-title" }, "월별 요양보호사와 수급자 수 추이", -1))
            ]),
            _: 1
          }),
          _createVNode(_component_v_card_text, null, {
            default: _withCtx(() => [
              _createElementVNode("canvas", {
                ref_key: "caregiverCountChart",
                ref: caregiverCountChart
              }, null, 512)
            ]),
            _: 1
          })
        ]),
        _: 1
      })
    ])
  ]))
}
}

})